/**
 * Comprehensive logout utility for the application
 * Handles auth state clearing, localStorage management, and navigation
 */

import { NavigateFunction } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { AuthContextType } from './context';
import { webStorage } from './storage';
import { localStorageVariables } from '@/types/localStorageVariables';
import { getPropertyId } from '@/hooks/useApiSequencing';
import { deleteCookie } from '@/lib/utils/cookie';

/**
 * Emergency auth clear - clears stored credentials without requiring React context.
 * Use this when:
 * - Auth context is not available (e.g., during early app initialization)
 * - 401 errors occur before logout handlers are set up
 * - Need to break out of stale credential loops
 * 
 * Called by sessionExpirationHandler when 401s occur before React context is ready.
 * 
 * This wrapper exists for two reasons:
 * 1. Error isolation — the try/catch ensures a failure during storage clearing
 *    never propagates to sessionExpirationHandler, which would break the
 *    hard-redirect fallback and leave the user stuck.
 * 2. Semantic intent — callers express "clear auth without context" rather than
 *    the lower-level "clear user items in localStorage".
 * 
 * Does NOT call the server logout endpoint — only clears local state.
 * For a complete logout with server notification, use performLogout() instead.
 */
export function clearAuthWithoutContext(): void {
  console.log('🔴 clearAuthWithoutContext: Clearing stale credentials without context');
  try {
    clearUserItemsInLocalStorage();
    console.log('✅ clearAuthWithoutContext: Credentials cleared successfully');
  } catch (error) {
    console.error('❌ clearAuthWithoutContext: Error clearing credentials:', error);
  }
}

/**
 * Global flag to indicate logout is in progress.
 * This is used by the API client to reject any requests during logout,
 * preventing stale API calls from components that are still mounted during
 * the logout/navigation process.
 */
let isLoggingOut = false;

/**
 * Check if logout is currently in progress.
 * Used by API clients to prevent requests during logout.
 */
export function isLogoutInProgress(): boolean {
  return isLoggingOut;
}

/**
 * Reset the logout flag. Called after navigation to login page completes.
 */
export function resetLogoutFlag(): void {
  isLoggingOut = false;
}

export interface LogoutOptions {
  /**
   * Navigation function from react-router-dom
   */
  navigate?: NavigateFunction;

  /**
   * Auth context with logout method
   */
  authLogout: AuthContextType['logout'];

  /**
   * Property from URL params (optional)
   */
  property?: string;

  /**
   * If true, do not navigate to login page, 
   * use when clearing auth state on hard navigation
   * from the top url bar. 
   */
  doNotNav?: boolean;
}

/**
 * Performs a complete logout including:
 * - Cancelling all React Query queries (prevents stale API calls)
 * - Clearing auth state via auth context
 * - Preserving specific localStorage items
 * - Clearing all localStorage and sessionStorage
 * - Restoring preserved items
 * - Navigating to property-specific login page
 */
export async function performLogout(options: LogoutOptions): Promise<void> {
  const { navigate, authLogout, property } = options;

  try {
    // STEP 0a: Set global logout flag IMMEDIATELY to prevent any new API calls
    // This is checked by the API client interceptor to reject requests during logout
    isLoggingOut = true;
    console.log('🔴 Logout: Setting logout flag to prevent new API calls');

    // STEP 0b: Cancel all React Query queries to stop in-flight requests
    const queryClient = (window as any).queryClient as QueryClient | undefined;
    if (queryClient) {
      console.log('🔴 Logout: Cancelling all React Query queries');
      queryClient.cancelQueries();
      // Also clear the query cache to ensure no stale data on re-login
      queryClient.clear();
    }

    const preservedItems = clearUserItemsInLocalStorage();

    await authLogout();

    console.log('? Logout: All stores, sessions, and cookies cleared');

    // Determine the property login path using URL property (sitename is cleared on logout)
    const siteName = property;
    let loginPath = '/login'; // Default fallback

    if (siteName) {
      loginPath = `/onlineleasingstudent/content3/access/${siteName}/guestlogin`;
    }

    // Navigate to property-specific login page
    if (!options.doNotNav) {
      navigate?.(loginPath);
    }

    // Reset logout flag after navigation to allow login page to make API calls
    // Use a small delay to ensure all stale component API calls have been blocked
    setTimeout(() => {
      isLoggingOut = false;
      console.log('✅ Logout: Reset logout flag after navigation');
    }, 500);
  } catch (error) {
    console.error('❌ Error during logout:', error);
    // Reset logout flag even on error to avoid blocking API calls forever
    isLoggingOut = false;
    // Even if there's an error, try to navigate to login
    // Use property from URL since sitename is cleared on logout
    const fallbackPath = property
      ? `/onlineleasingstudent/content3/access/${property}/guestlogin`
      : '/login';
    navigate?.(fallbackPath);
  }
}

export function clearUserItemsInLocalStorage(): any {
  // Preserve specific localStorage items before clearing
  // Note: sitename is NOT preserved - it should be removed on logout
  const preservedItems = {
    Property: localStorage.getItem('Property'),
    PropertyId: getPropertyId(),
    customtheme: localStorage.getItem('customtheme'),
    // sitename is intentionally NOT preserved - it should be cleared on logout
    PropertyName: localStorage.getItem(localStorageVariables.PropertyName),
    CachedThemeCSS: localStorage.getItem(localStorageVariables.CachedThemeCSS),
    CachedThemeCSSExpiry: localStorage.getItem(localStorageVariables.CachedThemeCSSExpiry),
    PropertyLogoUrl: localStorage.getItem(localStorageVariables.PropertyLogoUrl),
    PropertyBannerImageUrl: localStorage.getItem(localStorageVariables.PropertyBannerImageUrl),
    userpreferredlanguage: localStorage.getItem(localStorageVariables.UserPreferredLanguage) 
    };
    // Preserve secure-login-processed keys from sessionStorage (for deduplication)
    const preservedSessionItems: Record<string, string> = {};
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('secure-login-processed-')) {
            const value = sessionStorage.getItem(key);
            if (value !== null) {
                preservedSessionItems[key] = value;
            }
        }
    }

  // Clear all localStorage and sessionStorage for a complete logout
  localStorage.clear();
  sessionStorage.clear();
  webStorage.removeYsiSessionID();
  deleteCookie('liui', '/');

  // Restore only the preserved items after complete clear
  Object.entries(preservedItems).forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });

  // Restore preserved sessionStorage items (secure-login deduplication keys)
  Object.entries(preservedSessionItems).forEach(([key, value]) => {
    sessionStorage.setItem(key, value);
  });

  return preservedItems;
}